import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, MessageCircle } from "lucide-react";
import { LOGO_URL, FACEBOOK_URL, WHATSAPP_LINK, DEFAULT_PHONE, DEFAULT_EMAIL, DEFAULT_ADDRESS } from "@/lib/publicSite";

const FALLBACK_SERVICES = [
  { title: "Carpentry", slug: "carpentry" },
  { title: "General Repairs", slug: "general-repairs" },
  { title: "Furniture Builds", slug: "furniture-builds" },
];

export const PublicFooter = ({ bizInfo, services }) => {
  const svcList = services?.length ? services : FALLBACK_SERVICES;
  const phone = bizInfo?.business_phone || DEFAULT_PHONE;
  const email = bizInfo?.business_email || DEFAULT_EMAIL;
  const address = bizInfo?.business_address || DEFAULT_ADDRESS;

  return (
    <footer className="bg-primary text-primary-foreground py-8 sm:py-12" data-testid="public-footer">
      {/* WhatsApp Floating Button */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-50 bg-[#25D366] hover:bg-[#20bd5a] text-white p-3 sm:p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
        data-testid="whatsapp-button"
        aria-label="Contact us on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6 sm:h-7 sm:w-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <img src={LOGO_URL} alt="Sustainable Handyman Services" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover bg-white" />
              <span className="font-heading font-bold text-base sm:text-lg">Sustainable Handyman Services</span>
            </div>
            <p className="text-white/70 text-sm sm:text-base">Eco-friendly handyman services for sustainable homes.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Services</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-white/70 text-sm sm:text-base">
              {svcList.map((s) => (
                <li key={s.slug || s.title}>
                  {s.slug ? (
                    <Link to={`/services/${s.slug}`} className="hover:text-white transition-colors" data-testid={`footer-service-link-${s.slug}`}>
                      {s.title}
                    </Link>
                  ) : (
                    <span>{s.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="sm:col-span-2 md:col-span-1">
            <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Contact</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-white/70 text-sm sm:text-base">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-white" data-testid="footer-phone">{phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <a href={`mailto:${email}`} className="hover:text-white break-all text-xs sm:text-sm" data-testid="footer-email">{email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span className="text-xs sm:text-sm" data-testid="footer-address">{address}</span>
              </li>
              <li className="pt-2 flex flex-wrap gap-2">
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/10 hover:bg-white/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-colors text-xs sm:text-sm"
                  data-testid="facebook-link"
                >
                  <Facebook className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Facebook</span>
                </a>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 sm:gap-2 bg-green-600 hover:bg-green-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-colors text-white text-xs sm:text-sm"
                  data-testid="whatsapp-contact-link"
                >
                  <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>WhatsApp</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/20 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-white/50 text-xs sm:text-sm">
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("shs:open-cookie-settings"))}
              className="hover:text-white transition-colors underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded"
              data-testid="footer-cookie-settings"
            >
              Cookie Settings
            </button>
          </div>
          <p>© {new Date().getFullYear()} Sustainable Handyman Services. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};
